// components/ErrorBoundary.tsx
'use client';

import React, { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import Link from 'next/link';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

// 에러 폴백 컴포넌트
const ErrorFallback = ({ 
  error, 
  sectionName = "섹션",
  onRetry 
}: { 
  error?: Error; 
  sectionName?: string;
  onRetry: () => void;
}) => (
  <div className="w-full max-w-[1200px] mx-auto px-[10px] md:px-5">
    <div className="bg-white rounded-2xl shadow-xs border border-red-100 p-8 text-center">
      <div className="flex flex-col items-center space-y-4">
        {/* 에러 아이콘 */}
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-red-500" />
        </div>
        
        {/* 에러 메시지 */}
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {sectionName} 로딩 중 문제가 발생했습니다
          </h3>
          <p className="text-sm text-gray-600">
            인터넷 연결을 확인하시거나 잠시 후 다시 시도해주세요
          </p>
        </div>

        {/* 액션 버튼들 */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onRetry}
            className="inline-flex items-center gap-2 bg-[#A52A2A] text-white px-6 py-2 rounded-lg hover:bg-[#8B0000] transition-colors duration-200"
          >
            <RefreshCw className="w-4 h-4" />
            다시 시도
          </button>
          
          <Link
            href="/"
            className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
          >
            <Home className="w-4 h-4" />
            홈으로 이동
          </Link>
        </div>

        {/* 개발 환경에서만 에러 상세 정보 표시 */}
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mt-4 text-left">
            <summary className="text-sm text-gray-500 cursor-pointer">
              개발자 정보 (프로덕션에서는 숨겨짐)
            </summary>
            <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded overflow-auto">
              {error.message}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  </div>
);

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 에러 로깅 (실제 프로덕션에서는 Sentry 등 사용)
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // 에러 추적 서비스로 전송 (선택사항)
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
      // window.gtag?.('event', 'exception', {
      //   description: error.message,
      //   fatal: false,
      // });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 fallback이 있으면 사용, 없으면 기본 에러 UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          sectionName={this.props.sectionName}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}

// 함수형 컴포넌트용 에러 바운더리 HOC
export function withErrorBoundary<T extends object>(
  Component: React.ComponentType<T>,
  sectionName?: string
) {
  const WrappedComponent = (props: T) => (
    <ErrorBoundary sectionName={sectionName}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

// 간단한 에러 메시지 컴포넌트
export const SimpleErrorMessage = ({ message = "문제가 발생했습니다" }: { message?: string }) => (
  <div className="w-full max-w-[1200px] mx-auto px-[10px] md:px-5">
    <div className="bg-white rounded-2xl shadow-xs border border-yellow-200 p-6 text-center">
      <div className="flex items-center justify-center gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600" />
        <span className="text-gray-700">{message}</span>
      </div>
    </div>
  </div>
);

export default ErrorBoundary;